import type { LucideIcon } from "lucide-react";
import {
	ArmchairIcon,
	ChevronsDownIcon,
	ClapperboardIcon,
	ClockIcon,
	CombineIcon,
	DumbbellIcon,
	EraserIcon,
	FileTextIcon,
	FileVideo2Icon,
	FootprintsIcon,
	GemIcon,
	GoalIcon,
	HandHeartIcon,
	HandIcon,
	HeartHandshakeIcon,
	HeartIcon,
	ImageIcon,
	ImageUpIcon,
	ImagesIcon,
	LayersIcon,
	MicVocalIcon,
	MusicIcon,
	PaletteIcon,
	PartyPopperIcon,
	PersonStandingIcon,
	ReplaceIcon,
	ScissorsIcon,
	ShirtIcon,
	SmileIcon,
	SmilePlusIcon,
	SparklesIcon,
	SpeakerIcon,
	VideoIcon,
	WandSparklesIcon,
} from "lucide-react";

/** 站内导航的唯一数据源：NavBar 桌面下拉、手机抽屉、Footer 都从这里读。
 *  titleKey 是 next-intl 的完整 key，组件里直接 t(titleKey) */
export type SiteNavItem = {
	titleKey: string;
	href: string;
	icon: LucideIcon;
};

export type SiteNavGroup = {
	labelKey: string;
	items: SiteNavItem[];
};

const tpl = (id: string) =>
	`aiImageGenerator.templates.${id}.hero.title` as const;

export const AI_VIDEO_ITEMS: SiteNavItem[] = [
	{
		titleKey: "imageToVideo.title",
		href: "/image-to-video",
		icon: ClapperboardIcon,
	},
	{
		titleKey: "startEndFrame.title",
		href: "/start-end-frame-video",
		icon: LayersIcon,
	},
	{
		titleKey: "referenceToVideo.title",
		href: "/reference-to-video",
		icon: CombineIcon,
	},
	{
		titleKey: "genjutsuAi.title",
		href: "/genjutsu-ai",
		icon: ReplaceIcon,
	},
];

export const AI_IMAGE_ITEMS: SiteNavItem[] = [
	{
		titleKey: "aiImageGenerator.title",
		href: "/ai-image-generator",
		icon: WandSparklesIcon,
	},
	{
		titleKey: "aiEditImage.title",
		href: "/ai-edit-image",
		icon: SparklesIcon,
	},
	{
		titleKey: "imageToImage.title",
		href: "/image-to-image",
		icon: ImagesIcon,
	},
];

/** 单功能图片工具：跟 Studio 侧栏一致归 AI Tools，不进 navbar（2026-10-29 老公定），顺序同 /ai-tools 页 */
export const IMAGE_TOOL_ITEMS: SiteNavItem[] = [
	{
		titleKey: tpl("ai-clothes-changer"),
		href: "/templates/ai-clothes-changer",
		icon: ShirtIcon,
	},
	{
		titleKey: tpl("ai-pose-generator"),
		href: "/templates/ai-pose-generator",
		icon: PersonStandingIcon,
	},
	{
		titleKey: tpl("getty-images-watermark-remover"),
		href: "/templates/getty-images-watermark-remover",
		icon: EraserIcon,
	},
	{
		titleKey: tpl("ai-background-remover"),
		href: "/templates/ai-background-remover",
		icon: ImageUpIcon,
	},
	{
		titleKey: tpl("virtual-staging-ai"),
		href: "/templates/virtual-staging-ai",
		icon: ArmchairIcon,
	},
];

/** 照片特效：跟视频热梗一样只进 footer 不进 navbar（2026-10-29 老公定） */
export const AI_EFFECT_ITEMS: SiteNavItem[] = [
	{
		titleKey: "imageToAnime.title",
		href: "/photo-to-anime",
		icon: LayersIcon,
	},
	{
		titleKey: "imageToPixar.title",
		href: "/photo-to-pixar",
		icon: PaletteIcon,
	},
	{
		titleKey: "imageToCartoon.title",
		href: "/photo-to-cartoon",
		icon: PaletteIcon,
	},
	{ titleKey: "ageFilter.title", href: "/age-filter", icon: ClockIcon },
	{
		titleKey: tpl("ai-smile-generator"),
		href: "/templates/ai-smile-generator",
		icon: SmileIcon,
	},
	{
		titleKey: tpl("image-to-emoji-converter"),
		href: "/templates/image-to-emoji-converter",
		icon: SmilePlusIcon,
	},
	{ titleKey: "aiBuzzCut.title", href: "/ai-buzz-cut", icon: ScissorsIcon },
	{
		titleKey: tpl("ai-muscle-generator"),
		href: "/ai-muscle-generator",
		icon: DumbbellIcon,
	},
	{
		titleKey: tpl("ai-big-butt"),
		href: "/ai-big-butt",
		icon: SparklesIcon,
	},
	{
		titleKey: tpl("ai-feet-generator"),
		href: "/ai-feet-generator",
		icon: FootprintsIcon,
	},
];

/** 视频特效类内页：只进 footer 的 AI Video Tools 列，不进 navbar（2026-10-03 老公定） */
export const VIDEO_EFFECT_ITEMS: SiteNavItem[] = [
	{
		titleKey: "hotelLobby.title",
		href: "/hotel-lobby-ai-video-generator",
		icon: MicVocalIcon,
	},
	{
		titleKey: "chuttamalle.title",
		href: "/chuttamalle-ai-video-generator",
		icon: FootprintsIcon,
	},
	{
		titleKey: "zombieTrend.title",
		href: "/zombie-trend-ai-video-generator",
		icon: HeartIcon,
	},
	{
		titleKey: "rumpelstiltskin.title",
		href: "/rumpelstiltskin-ai-video-generator",
		icon: WandSparklesIcon,
	},
	{
		titleKey: "burningBridges.title",
		href: "/burning-bridges-ai-video-generator",
		icon: PartyPopperIcon,
	},
	{
		titleKey: "bulin47.title",
		href: "/bulin-47-ai-video-generator",
		icon: SpeakerIcon,
	},
	{
		titleKey: "ozilGoal.title",
		href: "/ozil-goal-ai-video-generator",
		icon: GoalIcon,
	},
	{
		titleKey: "teslaRoadster.title",
		href: "/tesla-roadster-ai-video-generator",
		icon: ClapperboardIcon,
	},
	{
		titleKey: "flyingCar.title",
		href: "/flying-car-ai",
		icon: ClapperboardIcon,
	},
	{
		titleKey: "aiFrenchKiss.title",
		href: "/ai-french-kiss-video-generator",
		icon: HeartIcon,
	},
	{
		titleKey: "tinyDesk.title",
		href: "/tiny-desk-ai-video-generator",
		icon: MicVocalIcon,
	},
	{
		titleKey: "transmission5Dance.title",
		href: "/transmission-5-dance-ai-video-generator",
		icon: FootprintsIcon,
	},
	{
		titleKey: "byeByeTrend.title",
		href: "/bye-bye-trend-ai-video-generator",
		icon: HandIcon,
	},
	{
		titleKey: "hopHopHopDance.title",
		href: "/hop-hop-hop-dance-ai-video-generator",
		icon: ChevronsDownIcon,
	},
	// 常青特效页（不是热梗）；互推区按特效页规矩选卡，见 video-trend-links.ts
	{
		titleKey: "aiKiss.title",
		href: "/ai-kiss-video-generator",
		icon: HeartHandshakeIcon,
	},
	{
		titleKey: "aiWedding.title",
		href: "/ai-wedding-video-generator",
		icon: GemIcon,
	},
	{
		titleKey: "aiHug.title",
		href: "/ai-hug-video-generator",
		icon: HandHeartIcon,
	},
	{
		titleKey: "aiAirKiss.title",
		href: "/ai-air-kiss-video-generator",
		icon: HeartIcon,
	},
	{
		titleKey: "aiTongueKiss.title",
		href: "/ai-tongue-kiss-generator",
		icon: HeartHandshakeIcon,
	},
	{
		titleKey: "aiDanceVideo.title",
		href: "/ai-dance-video-generator",
		icon: MusicIcon,
	},
];

/** 模型落地页：footer 单独一列（对标 imgveo 的 Video Models），不进 navbar */
export const VIDEO_MODEL_ITEMS: SiteNavItem[] = [
	{
		titleKey: "aiVideoGenerator.title",
		href: "/grok-ai-video-generator",
		icon: VideoIcon,
	},
	{
		titleKey: "seedance.title",
		href: "/seedance-2-ai-video-generator",
		icon: SparklesIcon,
	},
	{
		titleKey: "seedance25.title",
		href: "/seedance-2-5-ai-video-generator",
		icon: SparklesIcon,
	},
	{
		titleKey: "kling4.title",
		href: "/kling-4-ai-video-generator",
		icon: VideoIcon,
	},
];

/** Prompt 类工具有 SEO 流量但不是主业务，只进 footer 不进 navbar（2026-09-30 老公定） */
export const PROMPT_TOOL_ITEMS: SiteNavItem[] = [
	{
		titleKey: "common.footer.imageToPrompt",
		href: "/image-to-prompt",
		icon: ImageIcon,
	},
	{
		titleKey: "batchImageToPrompt.title",
		href: "/batch-image-to-prompt",
		icon: LayersIcon,
	},
	{
		titleKey: "common.footer.aiDescribeImage",
		href: "/ai-describe-image",
		icon: FileTextIcon,
	},
	{
		titleKey: "aiOutfitDescriber.hero.title",
		href: "/ai-outfit-describer",
		icon: ShirtIcon,
	},
	{
		titleKey: "veoPromptGenerator.title",
		href: "/veo-prompt-generator",
		icon: FileVideo2Icon,
	},
];

/** navbar 两个下拉（对标 imgveo：AI Video / AI Image），都只放核心三项，跟 Studio 侧栏对齐（2026-10-29 老公定） */
export const NAV_DROPDOWNS: SiteNavGroup[] = [
	{ labelKey: "saas.sidebar.aiVideo", items: AI_VIDEO_ITEMS },
	{ labelKey: "saas.sidebar.aiImage", items: AI_IMAGE_ITEMS },
];

/** footer 第一行：主入口，一组一列，跟 Studio 侧栏分组一致（2026-10-29 老公定） */
export const FOOTER_TOOL_GROUPS: SiteNavGroup[] = [
	{ labelKey: "saas.sidebar.aiVideo", items: AI_VIDEO_ITEMS },
	{ labelKey: "common.footer.videoModels", items: VIDEO_MODEL_ITEMS },
	{ labelKey: "saas.sidebar.aiImage", items: AI_IMAGE_ITEMS },
];

/**
 * footer 下面的标签条：每组占满一行、横排换行，页数再多也不拉长单列。
 * AI Tools 跟 /ai-tools 页一样是单功能工具 + Prompt 工具（2026-10-29 老公定）
 */
export const FOOTER_STRIP_GROUPS: SiteNavGroup[] = [
	{
		labelKey: "saas.sidebar.aiTools",
		items: [
			...IMAGE_TOOL_ITEMS,
			...PROMPT_TOOL_ITEMS,
			// 侧栏是顶级项，页脚只这一页不单开一列（11/03）
			{
				titleKey: "aiMusic.title",
				href: "/ai-music-generator",
				icon: MusicIcon,
			},
			{
				titleKey: "aiMusicVideo.hero.title",
				href: "/ai-music-video-generator",
				icon: MicVocalIcon,
			},
		],
	},
	{ labelKey: "common.footer.videoEffects", items: VIDEO_EFFECT_ITEMS },
	{ labelKey: "common.footer.photoEffects", items: AI_EFFECT_ITEMS },
];

export const isNavHrefActive = (pathname: string, href: string) =>
	href === "/" ? pathname === "/" : pathname.startsWith(href);
